/**
 * «Tu situación migratoria» (H-2): la pregunta del alta y su tarjeta en
 * Preferencias. Se guarda con el PATCH /job-apply/api/preferences de siempre,
 * con un cuerpo que lleva SOLO estas dos claves (como la tarjeta de países): el
 * formulario de las agencias no la toca, y si el servidor todavía no conociera
 * las claves (su esquema es `.strict()`), lo único que falla es este paso.
 *
 * No mueve el envío: cambia cómo presentamos a la persona en el correo, en la
 * hoja de vida del cuestionario y en las respuestas sugeridas.
 */
import type { JobPreferences } from './preferences';

/** Mismos valores que `H2_STATUSES` del servidor, en el orden en que se muestran. */
export const H2_STATUSES = ['first_time', 'returner', 'extension', 'transfer'] as const;
export type H2Status = (typeof H2_STATUSES)[number];

/** Mismo tope que el servidor (`H2_PREVIOUS_EMPLOYER_MAX`). */
export const H2_EMPLOYER_MAX = 120;

export function isH2Status(value: unknown): value is H2Status {
  return typeof value === 'string' && (H2_STATUSES as readonly string[]).includes(value);
}

/** Lo guardado → la selección. Ausente o basura = sin contestar. */
export function readH2Status(preferences?: JobPreferences | null): H2Status | null {
  const value = preferences?.h2_status;
  return isH2Status(value) ? value : null;
}

export function readH2Employer(preferences?: JobPreferences | null): string {
  return typeof preferences?.h2_previous_employer === 'string' ? preferences.h2_previous_employer : '';
}

/**
 * El campo «¿Con qué empleador y en qué temporada?» sólo aparece para quien ya
 * tuvo (o tiene) una H-2. Con «primera vez» no hay empleador que nombrar.
 */
export function asksForEmployer(status: H2Status | null): boolean {
  return status === 'returner' || status === 'extension' || status === 'transfer';
}

export interface H2Payload {
  h2_status: H2Status;
  h2_previous_employer: string | null;
}

/**
 * El cuerpo del PATCH. El empleador va recortado y con tope; vacío o con
 * «primera vez» viaja como null, que en el servidor BORRA lo que hubiera: quien
 * pasa de returner a primera vez no puede quedar con un empleador viejo.
 */
export function buildH2Payload(status: H2Status, employer: string): H2Payload {
  const clean = asksForEmployer(status) ? employer.trim().slice(0, H2_EMPLOYER_MAX).trim() : '';
  return { h2_status: status, h2_previous_employer: clean || null };
}

/* ── Papeles de la UE (Europa / EURES) ─────────────────────────────────────
 *
 * «¿Puedes trabajar legalmente en la Unión Europea?», para quien eligió Europa.
 * A diferencia de la situación H-2, ESTA respuesta sí mueve el envío: sin
 * papeles (o sin contestar) sólo salen postulaciones a vacantes que aceptan
 * candidatos de fuera de la UE.
 */

/** Mismos valores que `EU_WORK_AUTHORIZATIONS` del servidor, en el orden en que se muestran. */
export const EU_WORK_AUTHORIZATIONS = ['citizen', 'resident', 'none'] as const;
export type EuWorkAuthorization = (typeof EU_WORK_AUTHORIZATIONS)[number];

export function isEuWorkAuthorization(value: unknown): value is EuWorkAuthorization {
  return typeof value === 'string' && (EU_WORK_AUTHORIZATIONS as readonly string[]).includes(value);
}

/** Lo guardado → la selección. Ausente o basura = sin contestar. */
export function readEuAuthorization(preferences?: JobPreferences | null): EuWorkAuthorization | null {
  const value = preferences?.eu_work_authorization;
  return isEuWorkAuthorization(value) ? value : null;
}
